import { useRef, useEffect, useState } from 'react';
import $ from 'jquery';
import 'datatables.net-dt/js/dataTables.dataTables';
import 'datatables.net-buttons/js/dataTables.buttons';
import 'datatables.net-buttons/js/buttons.html5';
import 'jszip/dist/jszip';
import 'pdfmake/build/pdfmake';
import 'pdfmake/build/vfs_fonts';
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import PlantArrayActivityLayer from './PlantArrayActivityLayer'; // Import the new component

const ProvincialReportDepartmentSummary = ({ departmentData, province }) => {
  const tableRef = useRef(null);
  const [dataTable, setDataTable] = useState(null);
  const [totals, setTotals] = useState({
    totalUsers: 0,
    totalActivities: 0,
    totalPlants: 0
  });
  const [drillDownData, setDrillDownData] = useState(null);
  const [currentView, setCurrentView] = useState('departments'); // 'departments', 'districts', 'tehsils', 'activities'
  const [currentDepartment, setCurrentDepartment] = useState(null);
  const [currentDistrict, setCurrentDistrict] = useState(null);
  const [loading, setLoading] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const [selectedPlantationIds, setSelectedPlantationIds] = useState(null);
  
  const handleTehsilClick = (tehsil) => {
    if (!tehsil.plantations || !tehsil.plantations.length) return;
    
    // Extract plantation IDs from the tehsil data
    const plantationIds = tehsil.plantations.map(plantation => plantation._id || plantation.id);
    setSelectedPlantationIds(plantationIds);
    setCurrentView('activities');
  };

  // Calculate totals whenever departmentData changes
  useEffect(() => {
    if (departmentData && departmentData.length > 0 && currentView === 'departments') {
      const calculatedTotals = departmentData.reduce((acc, dept) => ({
        totalUsers: acc.totalUsers + (dept.totalUsers || 0),
        totalActivities: acc.totalActivities + (dept.totalActivities || 0),
        totalPlants: acc.totalPlants + (dept.totalPlants || 0)
      }), { totalUsers: 0, totalActivities: 0, totalPlants: 0 });
      
      setTotals(calculatedTotals);
    } else {
      setTotals({ totalUsers: 0, totalActivities: 0, totalPlants: 0 });
    }
  }, [departmentData, currentView]);

  useEffect(() => {
    // Store the current tableRef value in a variable for cleanup
    const currentTableRef = tableRef.current;

    if (departmentData && departmentData.length > 0 && currentTableRef && currentView === 'departments') {
      // Destroy existing DataTable if it exists
      if ($.fn.DataTable.isDataTable(currentTableRef)) {
        $(currentTableRef).DataTable().destroy();
      }

      // Initialize DataTable with export buttons and no pagination
      const table = $(currentTableRef).DataTable({
        dom: 'Bfrtip',
        buttons: [
          {
            extend: 'csv',
            text: 'Export CSV',
            className: 'btn-csv-export',
            exportOptions: {
              columns: ':visible'
            }
          }
        ],
        destroy: true,
        responsive: true,
        paging: false,
        searching: true,
        info: false,
        autoWidth: false,
        order: [],
        columnDefs: [
          {
            targets: 0, // Sr.No. column
            data: null,
            render: function (data, type, row, meta) {
              return meta.row + 1; // Serial number starting from 1
            },
            width: '50px', // Set a minimal width for Sr.No. column
            className: 'text-center' // Center-align the content
          }
        ],
        footerCallback: function (row, data, start, end, display) {
          const api = this.api();
          
          // Update footer with calculated totals
          $(api.column(2).footer()).html(totals.totalUsers);
          $(api.column(3).footer()).html(totals.totalActivities);
          $(api.column(4).footer()).html(totals.totalPlants.toLocaleString());
        }
      });

      setDataTable(table);
    }

    // Cleanup function to destroy DataTable when component unmounts
    return () => {
      if (currentTableRef && $.fn.DataTable.isDataTable(currentTableRef)) {
        $(currentTableRef).DataTable().destroy();
      }
    };
  }, [departmentData, totals, currentView]); // Removed dataTable from dependencies

  const fetchDepartmentStats = async (departmentName, district = null, tehsil = null) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      // Prepare request data with province
      const requestData = {
        province: province, // Use the province prop
        name: departmentName
      };
      
      // Add district if provided
      if (district) {
        requestData.district = district;
      }
      
      // Add tehsil if provided
      if (tehsil) {
        requestData.tehsil = tehsil;
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/api/plantation/pafs/department-summary`,
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.data.success) {
        setDrillDownData(response.data.data);
        return response.data.data;
      }
    } catch (error) {
      console.error("Error fetching department stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDepartmentClick = async (department) => {
    setCurrentDepartment(department.departmentName);
    const data = await fetchDepartmentStats(department.departmentName);
    if (data) {
      setCurrentView('districts');
    }
  };

  const handleDistrictClick = async (district) => {
    setCurrentDistrict(district.district || district.tehsil);
    const data = await fetchDepartmentStats(currentDepartment, district.district || district.tehsil);
    if (data) {
      setCurrentView('tehsils');
    }
  };

  const handleBackClick = () => {
    if (currentView === 'activities') {
      setCurrentView('tehsils');
      setSelectedPlantationIds(null);
    } else if (currentView === 'tehsils') {
      setCurrentView('districts');
      setCurrentDistrict(null);
    } else if (currentView === 'districts') {
      setCurrentView('departments');
      setCurrentDepartment(null);
      setDrillDownData(null);
    }
  };

  const exportToCSV = () => {
    if (dataTable && currentView === 'departments') {
      dataTable.button('.btn-csv-export').trigger();
    }
  };

  const renderDepartmentsTable = () => {
    return (
      <>
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">{province}'s Department Summary</h5>
          <button 
            onClick={exportToCSV} 
            className="btn btn-success btn-sm me-2 d-flex align-items-center"
            disabled={!departmentData || departmentData.length === 0}
          >
            <Icon icon="mdi:file-delimited-outline" className="me-1" style={{ fontSize: '16px' }} /> 
            Download CSV
          </button>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table 
              ref={tableRef} 
              className="table vertical-striped-table mb-0 display"
              style={{ width: '100%' }}
            >
              <thead>
                <tr>
                  <th scope="col" style={{ width: '50px', textAlign: 'center' }}>Sr.No.</th>
                  <th scope="col">Department Name</th>
                  <th scope="col">Total Users</th>
                  <th scope="col">Total Activities</th>
                  <th scope="col">Total Plants</th>
                </tr>
              </thead>
              <tbody>
                {departmentData && departmentData.length > 0 ? (
                  departmentData.map((dept, index) => (
                    <tr key={index}>
                      <td style={{ width: '50px', textAlign: 'center' }}>{index + 1}</td>
                      <td>
                        <h6 
                          className="text-md mb-0 fw-normal cursor-pointer text-primary" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleDepartmentClick(dept)}
                        >
                          {dept.departmentName || 'Unknown Department'}
                        </h6>
                      </td>
                      <td>{dept.totalUsers}</td>
                      <td>{dept.totalActivities}</td>
                      <td>{dept.totalPlants.toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-20">
                      <div className="d-flex flex-column align-items-center">
                        <Icon icon="mdi:database-off" className="text-3xl text-muted mb-10" />
                        <p className="text-muted">No department data available</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                {departmentData && departmentData.length > 0 && (
                  <tr className="fw-bold">
                    <td style={{ width: '50px' }}></td>
                    <td>TOTAL</td>
                    <td>{totals.totalUsers}</td>
                    <td>{totals.totalActivities}</td>
                    <td>{totals.totalPlants.toLocaleString()}</td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        </div>
      </>
    );
  };
  
  const renderDistrictsTable = () => {
    return (
      <>
        <div className="card-header d-flex justify-content-between align-items-center">
          <button 
            onClick={handleBackClick} 
            className="btn btn-secondary btn-sm d-flex align-items-center"
          >
            <Icon icon="mdi:arrow-left" className="me-1" style={{ fontSize: '16px' }} /> 
            Back to Departments
          </button>
          <h5 className="card-title mb-0">
            {currentDepartment} - Districts
          </h5>
          <div></div> {/* Empty div for spacing */}
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table vertical-striped-table mb-0" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th scope="col" style={{ width: '50px', textAlign: 'center' }}>Sr.No.</th>
                  <th scope="col">District Name</th>
                  <th scope="col">Active Users</th>
                  <th scope="col">Total Activities</th>
                  <th scope="col">Total Plants</th>
                </tr>
              </thead>
              <tbody>
                {drillDownData && drillDownData.length > 0 ? (
                  drillDownData.map((district, index) => (
                    <tr key={index}>
                      <td style={{ width: '50px', textAlign: 'center' }}>{index + 1}</td>
                      <td>
                        <h6 
                          className="text-md mb-0 fw-normal cursor-pointer text-primary" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleDistrictClick(district)}
                        >
                          {district.district || 'Unknown District'}
                        </h6>
                      </td>
                      <td>{district.active_users}</td>
                      <td>{district.totalActivities}</td>
                      <td>{district.totalPlants.toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-20">
                      <div className="d-flex flex-column align-items-center">
                        <Icon icon="mdi:database-off" className="text-3xl text-muted mb-10" />
                        <p className="text-muted">No district data available</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderTehsilsTable = () => {
    return (
      <>
        <div className="card-header d-flex justify-content-between align-items-center">
          <button 
            onClick={handleBackClick} 
            className="btn btn-secondary btn-sm d-flex align-items-center"
          >
            <Icon icon="mdi:arrow-left" className="me-1" style={{ fontSize: '16px' }} /> 
            Back to Districts
          </button>
          <h5 className="card-title mb-0">
            {currentDepartment} - {currentDistrict} - Tehsils
          </h5>
          <div></div> {/* Empty div for spacing */}
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table vertical-striped-table mb-0" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th scope="col" style={{ width: '50px', textAlign: 'center' }}>Sr.No.</th>
                  <th scope="col">Tehsil Name</th>
                  <th scope="col">Active Users</th>
                  <th scope="col">Total Activities</th>
                  <th scope="col">Total Plants</th>
                </tr>
              </thead>
              <tbody>
                {drillDownData && drillDownData.length > 0 ? (
                  drillDownData.map((tehsil, index) => (
                    <tr key={index}>
                      <td style={{ width: '50px', textAlign: 'center' }}>{index + 1}</td>
                      <td>
                          <h6
                            className="text-md mb-0 fw-normal cursor-pointer text-primary"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleTehsilClick(tehsil)}
                          >
                            {tehsil.tehsil || 'Unknown Tehsil'}
                          </h6>
                        </td>
                      <td>{tehsil.active_users}</td>
                      <td>{tehsil.totalActivities}</td>
                      <td>{tehsil.totalPlants.toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-20">
                      <div className="d-flex flex-column align-items-center">
                        <Icon icon="mdi:database-off" className="text-3xl text-muted mb-10" />
                        <p className="text-muted">No tehsil data available</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderActivitiesView = () => {
    return (
      <>
        <div className="card-header d-flex justify-content-between align-items-center">
          <button 
            onClick={handleBackClick} 
            className="btn btn-secondary btn-sm d-flex align-items-center"
          >
            <Icon icon="mdi:arrow-left" className="me-1" style={{ fontSize: '16px' }} /> 
            Back to Tehsils
          </button>
          <h5 className="card-title mb-0">
            {currentDepartment} - {currentDistrict} - Plantation Activities
          </h5>
          <div></div> {/* Empty div for spacing */}
        </div>
        <div className="card-body">
          <PlantArrayActivityLayer ids={selectedPlantationIds} />
        </div>
      </>
    );
  };

  return (
    <div className="card">
      {loading && (
        <div className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center" style={{ background: 'rgba(255,255,255,0.7)', zIndex: 1000 }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      
      {currentView === 'departments' && renderDepartmentsTable()}
      {currentView === 'districts' && renderDistrictsTable()}
      {currentView === 'tehsils' && renderTehsilsTable()}
      {currentView === 'activities' && renderActivitiesView()}
    </div>
  );
};

export default ProvincialReportDepartmentSummary;